// src/features/kommunikation/services/CredentialsService.ts
//
// ZUGANGSDATEN AUS DATEV (05.10.2026, Freigabe Tim):
//   Bei Kunden mit Lohn und Gehalt und gültiger hr:exchange-Freischaltung ist
//   DATEV die einzige Quelle für E-Mail und IBAN. Ein Datenbank-Trigger auf dem
//   Mitarbeiterspiegel (zugangsdaten_aus_spiegel) legt die Zugangsdaten dieser
//   Kunden an und hält sie aktuell. Dieser Service ergänzt das um die
//   Gegenrichtung:
//     - speichereMitDatev: Wird bei einem solchen Kunden eine E-Mail-Adresse
//       erfasst oder geändert, die DATEV noch nicht kennt, wird sie als
//       Stammdatenänderung an DATEV übermittelt (datev_schreibauftrag_anlegen,
//       Quelle 'kommunikationstool'). Der Zugangsdatensatz wird sofort
//       gespeichert, damit ein manueller Versand gleich möglich ist. Das
//       Passwort kommt aus der IBAN in DATEV, sofern dort hinterlegt.
//     - importFromCSV: Zeilen dieser Kunden werden übersprungen und als
//       "aus DATEV" ausgewiesen, weil ihre Zugangsdaten automatisch entstehen.
//   Alle übrigen Kunden (LODAS, ohne hr:exchange) behalten das bisherige
//   Verhalten unverändert.

import { supabase } from '../../../shared/services/supabase';
import {
  CommunicationCredential,
  CredentialCSVRow,
  CredentialImportResult,
  CredentialLookup,
  CredentialSpeicherErgebnis,
  EmailAuftragStand,
  ZugangsdatenPruefung
} from '../types';

// Blockgröße beim Laden aller Zugangsdaten (siehe getAllCredentials)
const CREDENTIALS_BLOCK = 1000;

/**
 * Mappt Geschlecht-String zu Anrede
 */
function mapGeschlechtToAnrede(geschlecht: string): 'Herr' | 'Frau' | 'Divers' | null {
  const normalized = geschlecht.toLowerCase().trim();
  if (normalized === 'männlich' || normalized === 'm' || normalized === 'herr') return 'Herr';
  if (normalized === 'weiblich' || normalized === 'w' || normalized === 'frau') return 'Frau';
  if (normalized === 'divers' || normalized === 'd') return 'Divers';
  return null;
}

/**
 * Laufender Kalendermonat in Europe/Berlin als "YYYY-MM" (Bezugsmonat für
 * Stammdatenänderungen, die keine Gültigkeit haben).
 */
function laufenderMonatBerlin(): string {
  return new Intl.DateTimeFormat('sv-SE', { timeZone: 'Europe/Berlin' }).format(new Date()).substring(0, 7);
}

/**
 * Schlüssel für Nachschlagetabellen je Kunde bzw. je Mitarbeiter
 */
export function kundenSchluessel(beraternummer: string, mandantennummer: string): string {
  return `${beraternummer}-${mandantennummer}`;
}

export function mitarbeiterSchluessel(beraternummer: string, mandantennummer: string, personalnummer: string): string {
  return `${beraternummer}-${mandantennummer}-${personalnummer}`;
}

/**
 * Service für Verwaltung von E-Mail-Credentials und CSV-Import
 */
export class CredentialsService {
  
  /**
   * Lädt alle aktiven Credentials
   *
   * KORREKTUR (05.10.2026): Die Datenbankschnittstelle liefert pro Abfrage nur
   * eine begrenzte Zahl von Zeilen (Supabase-Standard 1.000). Eine einzelne
   * Abfrage hat deshalb nicht alle Zugangsdaten geliefert, und die Liste samt
   * Suche war unvollständig. Geladen wird jetzt blockweise, bis keine Zeilen
   * mehr kommen. Die zusätzliche Sortierung nach id hält die Reihenfolge
   * zwischen den Blöcken stabil, auch wenn viele Datensätze denselben
   * Anlagezeitpunkt haben.
   */
  async getAllCredentials(): Promise<CommunicationCredential[]> {
    try {
      const alle: CommunicationCredential[] = [];
      let von = 0;
      // Obergrenze gegen eine Endlosschleife bei einem unerwarteten Fehler der
      // Schnittstelle: 200 Blöcke reichen für weit über 100.000 Datensätze.
      for (let block = 0; block < 200; block++) {
        const { data, error } = await supabase
          .from('communication_credentials')
          .select('*')
          .eq('aktiv', true)
          .order('created_at', { ascending: false })
          .order('id', { ascending: true })
          .range(von, von + CREDENTIALS_BLOCK - 1);
        
        if (error) {
          console.error('Error loading credentials:', error);
          throw new Error(`Credentials laden fehlgeschlagen: ${error.message}`);
        }
        
        const zeilen = (data || []) as CommunicationCredential[];
        if (zeilen.length === 0) break;
        alle.push(...zeilen);
        von += zeilen.length;
      }
      
      return alle;
    } catch (error) {
      console.error('Error in getAllCredentials:', error);
      throw error;
    }
  }
  
  /**
   * Sucht Credential für bestimmte Kombination
   */
  async findCredential(lookup: CredentialLookup): Promise<CommunicationCredential | null> {
    try {
      const { data, error } = await supabase
        .from('communication_credentials')
        .select('*')
        .eq('beraternummer', lookup.beraternummer)
        .eq('mandantennummer', lookup.mandantennummer)
        .eq('personalnummer', lookup.personalnummer)
        .eq('aktiv', true)
        .single();
      
      if (error) {
        if (error.code === 'PGRST116') {
          return null;
        }
        console.error('Error finding credential:', error);
        return null;
      }
      
      return data;
    } catch (error) {
      console.error('Error in findCredential:', error);
      return null;
    }
  }
  
  /**
   * Erstellt oder aktualisiert ein Credential
   */
  async upsertCredential(
    credential: Omit<CommunicationCredential, 'id' | 'created_at' | 'updated_at'>
  ): Promise<{ success: boolean; error?: string }> {
    try {
      const { error } = await supabase
        .from('communication_credentials')
        .upsert({
          beraternummer: credential.beraternummer,
          mandantennummer: credential.mandantennummer,
          personalnummer: credential.personalnummer,
          email: credential.email,
          passwort: credential.passwort,
          anrede: credential.anrede,
          nachname: credential.nachname,
          aktiv: credential.aktiv,
          created_by_email: credential.created_by_email
        }, {
          onConflict: 'beraternummer,mandantennummer,personalnummer'
        });
      
      if (error) {
        console.error('Error upserting credential:', error);
        return {
          success: false,
          error: `Speichern fehlgeschlagen: ${error.message}`
        };
      }
      
      return { success: true };
    } catch (error) {
      console.error('Error in upsertCredential:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Unbekannter Fehler'
      };
    }
  }

  // ============================================
  // ZUGANGSDATEN AUS DATEV (siehe Dateikopf)
  // ============================================

  /**
   * Prüft, ob die Zugangsdaten dieses Mitarbeiters aus DATEV kommen, und
   * liefert den Stand im Mitarbeiterspiegel.
   */
  async pruefeZugangsdaten(lookup: CredentialLookup): Promise<ZugangsdatenPruefung> {
    const { data, error } = await supabase.rpc('kommunikation_zugangsdaten_pruefen', {
      p_beraternummer: lookup.beraternummer,
      p_mandantennummer: lookup.mandantennummer,
      p_personalnummer: lookup.personalnummer
    });
    if (error) {
      throw new Error(`DATEV-Prüfung fehlgeschlagen: ${error.message}`);
    }
    return (data ?? { datev_quelle: false, customer_id: null }) as ZugangsdatenPruefung;
  }

  /**
   * Kunden, deren Zugangsdaten aus DATEV kommen, als Menge von
   * "Beraternummer-Mandantennummer".
   */
  async ladeDatevKunden(): Promise<Set<string>> {
    const { data, error } = await supabase.rpc('kommunikation_datev_kunden');
    if (error) {
      throw new Error(`DATEV-Kunden laden fehlgeschlagen: ${error.message}`);
    }
    const menge = new Set<string>();
    for (const zeile of (data ?? []) as Array<{ beraternummer: string; mandantennummer: string }>) {
      menge.add(kundenSchluessel(zeile.beraternummer, zeile.mandantennummer));
    }
    return menge;
  }

  /**
   * Jüngster E-Mail-Änderungsauftrag aus dem Kommunikationstool je
   * Mitarbeiter, Schlüssel "Berater-Mandant-Personalnummer".
   */
  async ladeEmailAuftraege(): Promise<Map<string, EmailAuftragStand>> {
    const { data, error } = await supabase.rpc('kommunikation_email_auftraege');
    if (error) {
      throw new Error(`DATEV-Aufträge laden fehlgeschlagen: ${error.message}`);
    }
    const karte = new Map<string, EmailAuftragStand>();
    for (const zeile of (data ?? []) as EmailAuftragStand[]) {
      karte.set(mitarbeiterSchluessel(zeile.beraternummer, zeile.mandantennummer, zeile.personalnummer), zeile);
    }
    return karte;
  }

  /**
   * Übermittelt eine E-Mail-Adresse als Stammdatenänderung an DATEV.
   */
  private async emailAnDatev(
    customerId: string,
    personalnummer: string,
    email: string,
    nachname: string,
    vorname: string | null
  ): Promise<{ success: boolean; error?: string }> {
    const payload: Record<string, unknown> = {
      surname: nachname,
      personal_data: { email }
    };
    if (vorname) payload.first_name = vorname;

    const { error } = await supabase.rpc('datev_schreibauftrag_anlegen', {
      p_customer_id: customerId,
      p_personnel_number: personalnummer,
      p_reference_month: laufenderMonatBerlin(),
      p_quelle: 'kommunikationstool',
      p_basis_fingerabdruck_kalendarium: null,
      p_basis_fingerabdruck_monatswerte: null,
      p_positionen: [{ art: 'stammdaten_aenderung', feld: 'email', payload }]
    });
    if (error) {
      return { success: false, error: error.message };
    }
    return { success: true };
  }

  /**
   * Speichert einen Zugangsdatensatz. Bei Kunden, deren Zugangsdaten aus
   * DATEV kommen, wird das Passwort aus der IBAN in DATEV übernommen und eine
   * neue E-Mail-Adresse zusätzlich an DATEV übermittelt. Für alle übrigen
   * Kunden entspricht das Verhalten upsertCredential.
   */
  async speichereMitDatev(
    credential: Omit<CommunicationCredential, 'id' | 'created_at' | 'updated_at'>
  ): Promise<CredentialSpeicherErgebnis> {
    let pruefung: ZugangsdatenPruefung;
    try {
      pruefung = await this.pruefeZugangsdaten({
        beraternummer: credential.beraternummer,
        mandantennummer: credential.mandantennummer,
        personalnummer: credential.personalnummer
      });
    } catch (err) {
      return { success: false, error: err instanceof Error ? err.message : 'DATEV-Prüfung fehlgeschlagen' };
    }

    if (!pruefung.datev_quelle) {
      return this.upsertCredential(credential);
    }

    // Passwort aus der IBAN in DATEV, sonst die Eingabe
    const passwort = pruefung.spiegel_iban4 || credential.passwort;
    if (!passwort) {
      return {
        success: false,
        error: 'In DATEV ist keine IBAN hinterlegt. Bitte die letzten 4 Stellen der IBAN als Passwort eingeben.'
      };
    }

    const gespeichert = await this.upsertCredential({ ...credential, passwort });
    if (!gespeichert.success) {
      return gespeichert;
    }

    const neueEmail = credential.email.trim();
    const datevEmail = (pruefung.spiegel_email || '').trim();
    if (neueEmail.toLowerCase() === datevEmail.toLowerCase()) {
      return { success: true };
    }

    if (!pruefung.im_spiegel || !pruefung.customer_id) {
      return {
        success: true,
        hinweis: 'Der Mitarbeiter steht nicht im DATEV-Spiegel. Die E-Mail-Adresse ist nur im Kommunikationstool gespeichert.'
      };
    }

    const nachname = pruefung.spiegel_nachname || credential.nachname || '';
    if (!nachname) {
      return {
        success: true,
        hinweis: 'Die E-Mail-Adresse ist gespeichert, konnte aber nicht an DATEV übermittelt werden: Nachname fehlt.'
      };
    }

    const anDatev = await this.emailAnDatev(
      pruefung.customer_id,
      credential.personalnummer,
      neueEmail,
      nachname,
      pruefung.spiegel_vorname ?? null
    );
    if (!anDatev.success) {
      return {
        success: true,
        hinweis: `Die E-Mail-Adresse ist gespeichert, die Übermittlung an DATEV ist aber fehlgeschlagen: ${anDatev.error}`
      };
    }

    return {
      success: true,
      hinweis: 'Die E-Mail-Adresse wird an DATEV übermittelt. Nach der Bestätigung durch DATEV ist sie im Spiegel hinterlegt.'
    };
  }
  
  /**
   * Deaktiviert ein Credential
   */
  async deactivateCredential(id: string): Promise<{ success: boolean; error?: string }> {
    try {
      const { error } = await supabase
        .from('communication_credentials')
        .update({ aktiv: false })
        .eq('id', id);
      
      if (error) {
        console.error('Error deactivating credential:', error);
        return {
          success: false,
          error: `Deaktivieren fehlgeschlagen: ${error.message}`
        };
      }
      
      return { success: true };
    } catch (error) {
      console.error('Error in deactivateCredential:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Unbekannter Fehler'
      };
    }
  }
  
  /**
   * Liest Datei mit korrektem Encoding (UTF-8 oder Windows-1252)
   * DATEV-Dateien aus Windows sind oft in Windows-1252 kodiert
   */
  private async readFileWithEncoding(file: File): Promise<string> {
    const arrayBuffer = await file.arrayBuffer();
    const uint8Array = new Uint8Array(arrayBuffer);
    
    // Prüfe auf UTF-8 BOM
    const hasUtf8Bom = uint8Array[0] === 0xEF && uint8Array[1] === 0xBB && uint8Array[2] === 0xBF;
    if (hasUtf8Bom) {
      return new TextDecoder('utf-8').decode(uint8Array);
    }
    
    // Versuche UTF-8
    try {
      const utf8Text = new TextDecoder('utf-8', { fatal: true }).decode(uint8Array);
      if (!utf8Text.includes('\uFFFD')) {
        return utf8Text;
      }
    } catch (e) {
      // UTF-8 fehlgeschlagen
    }
    
    // Fallback: Windows-1252
    return new TextDecoder('windows-1252').decode(uint8Array);
  }
  
  /**
   * Parst CSV-Datei und gibt Zeilen zurück
   * NEUES FORMAT: Semikolon-Delimiter, neue Spaltennamen
   */
  async parseCSV(file: File): Promise<{ success: boolean; rows?: CredentialCSVRow[]; error?: string }> {
    try {
      const text = await this.readFileWithEncoding(file);
      const lines = text.split('\n').filter(line => line.trim().length > 0);
      
      if (lines.length < 2) {
        return {
          success: false,
          error: 'CSV-Datei enthält keine Daten'
        };
      }
      
      // WICHTIG: Delimiter ist jetzt Semikolon (;)
      const header = lines[0].split(';').map(h => h.trim());
      
      // Neue Header-Namen aus dem realen Format
      const expectedHeaders = [
        'Berater',
        'Mandant',
        'Mitarbeiternummer',
        'EMail Mitarbeiter',
        'IBAN der Mitarbeiterbankverbindung',
        'Geschlecht des Mitarbeiters - lang',
        'Familienname des Mitarbeiters (Personaldaten)'
      ];
      
      const missingHeaders = expectedHeaders.filter(h => !header.includes(h));
      if (missingHeaders.length > 0) {
        return {
          success: false,
          error: `Fehlende Spalten: ${missingHeaders.join(', ')}`
        };
      }
      
      const rows: CredentialCSVRow[] = [];
      const errors: string[] = [];
      
      for (let i = 1; i < lines.length; i++) {
        const values = lines[i].split(';').map(v => v.trim());
        
        // Spaltenanzahl nicht prüfen, da CSV mehr Spalten haben kann (die ignoriert werden)
        if (values.length < expectedHeaders.length) {
          errors.push(`Zeile ${i + 1}: Zu wenige Spalten`);
          continue;
        }
        
        // Row-Object mit neuem Mapping erstellen
        const row: any = {};
        header.forEach((key, index) => {
          if (values[index]) {
            row[key] = values[index];
          }
        });
        
        // Validierung der Pflichtfelder
        if (!row.Berater || !row.Mandant || !row.Mitarbeiternummer || 
            !row['EMail Mitarbeiter'] || !row['IBAN der Mitarbeiterbankverbindung'] || 
            !row['Geschlecht des Mitarbeiters - lang'] || !row['Familienname des Mitarbeiters (Personaldaten)']) {
          errors.push(`Zeile ${i + 1}: Fehlende Pflichtfelder`);
          continue;
        }
        
        // E-Mail-Validierung
        if (!row['EMail Mitarbeiter'].includes('@')) {
          errors.push(`Zeile ${i + 1}: Ungültige E-Mail-Adresse`);
          continue;
        }
        
        // IBAN-Validierung (mindestens 15 Zeichen)
        const iban = row['IBAN der Mitarbeiterbankverbindung'].replace(/\s/g, '');
        if (iban.length < 15) {
          errors.push(`Zeile ${i + 1}: IBAN zu kurz (mindestens 15 Zeichen erforderlich)`);
          continue;
        }
        
        // IBAN sollte mit 2 Buchstaben (Ländercode) beginnen und nur Buchstaben/Zahlen enthalten
        if (!/^[A-Z]{2}[A-Z0-9]+$/i.test(iban)) {
          errors.push(`Zeile ${i + 1}: Ungültiges IBAN-Format`);
          continue;
        }
        
        rows.push(row as CredentialCSVRow);
      }
      
      if (rows.length === 0) {
        return {
          success: false,
          error: errors.length > 0 ? errors.join('; ') : 'Keine gültigen Zeilen gefunden'
        };
      }
      
      return {
        success: true,
        rows
      };
      
    } catch (error) {
      console.error('CSV parsing error:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'CSV-Parsing fehlgeschlagen'
      };
    }
  }
  
  /**
   * Importiert Credentials aus CSV
   * NEU: Extrahiert letzte 4 Stellen der IBAN als Passwort
   * NEU (05.10.2026): Zeilen von Kunden, deren Zugangsdaten aus DATEV kommen,
   * werden nicht importiert und als ausDatev gezählt.
   */
  async importFromCSV(
    file: File,
    uploadedByEmail: string
  ): Promise<CredentialImportResult> {
    try {
      const parseResult = await this.parseCSV(file);
      if (!parseResult.success || !parseResult.rows) {
        return {
          success: false,
          imported: 0,
          updated: 0,
          skipped: 0,
          errors: [parseResult.error || 'CSV-Parsing fehlgeschlagen']
        };
      }

      // Kunden mit Zugangsdaten aus DATEV. Ohne diese Information wird nicht
      // importiert, damit keine Zeile eines solchen Kunden versehentlich die
      // automatisch gepflegten Zugangsdaten überschreibt.
      let datevKunden: Set<string>;
      try {
        datevKunden = await this.ladeDatevKunden();
      } catch (err) {
        return {
          success: false,
          imported: 0,
          updated: 0,
          skipped: 0,
          errors: [err instanceof Error ? err.message : 'DATEV-Kunden konnten nicht geladen werden']
        };
      }
      
      const rows = parseResult.rows;
      let imported = 0;
      let updated = 0;
      let skipped = 0;
      let ausDatev = 0;
      const errors: string[] = [];
      
      for (const row of rows) {
        if (datevKunden.has(kundenSchluessel(row.Berater, row.Mandant))) {
          ausDatev++;
          continue;
        }

        const normalizedPersonalnummer = parseInt(row.Mitarbeiternummer, 10).toString();
        const anrede = mapGeschlechtToAnrede(row['Geschlecht des Mitarbeiters - lang']);
        
        // WICHTIG: Extrahiere letzte 4 Stellen der IBAN als Passwort
        const iban = row['IBAN der Mitarbeiterbankverbindung'].replace(/\s/g, '');
        const letzteVierStellen = iban.slice(-4);
        
        try {
          const existing = await this.findCredential({
            beraternummer: row.Berater,
            mandantennummer: row.Mandant,
            personalnummer: normalizedPersonalnummer
          });
          
          const result = await this.upsertCredential({
            beraternummer: row.Berater,
            mandantennummer: row.Mandant,
            personalnummer: normalizedPersonalnummer,
            email: row['EMail Mitarbeiter'],
            passwort: letzteVierStellen, // ← Letzte 4 Stellen der IBAN
            anrede: anrede,
            nachname: row['Familienname des Mitarbeiters (Personaldaten)'],
            aktiv: true,
            created_by_email: uploadedByEmail
          });
          
          if (result.success) {
            if (existing) {
              updated++;
            } else {
              imported++;
            }
          } else {
            skipped++;
            errors.push(`${row.Mandant}/${normalizedPersonalnummer}: ${result.error}`);
          }
          
        } catch (error) {
          skipped++;
          errors.push(`${row.Mandant}/${normalizedPersonalnummer}: ${error instanceof Error ? error.message : 'Fehler'}`);
        }
      }
      
      return {
        success: imported + updated > 0,
        imported,
        updated,
        skipped,
        errors,
        ausDatev
      };
      
    } catch (error) {
      console.error('CSV import error:', error);
      return {
        success: false,
        imported: 0,
        updated: 0,
        skipped: 0,
        errors: [error instanceof Error ? error.message : 'Import fehlgeschlagen']
      };
    }
  }
}

export const credentialsService = new CredentialsService();
